import type { ImageLoaderConfig } from '@angular/common';

import { isAbsoluteUrl, isValidUrl } from '@cosmos/util-common';

// @angular/common doesn't export imxImageLoader as a function, only the provider
// so we have to create our own loader function

const PLACEHOLDER_QUALITY = 20;

export function createImgixLoader(
  path: string
): (config: ImageLoaderConfig) => string {
  if (!isValidUrl(path)) {
    throw new Error(`imgixLoader: Invalid imgix path: ${path}`);
  }

  path = path.endsWith('/') ? path.slice(0, -1) : path;

  return function loaderFn(config: ImageLoaderConfig) {
    if (isAbsoluteUrl(config.src)) {
      throw new Error(`imgixLoader: Unexpected absolute URL: ${config.src}`);
    }

    // Remove leading slash from src
    const src = config.src.replace(/^\//, '');
    const url = new URL(`${path}/${src}`);

    // This setting ensures the smallest allowable format is set.
    url.searchParams.set('auto', 'format');

    if (config.width) {
      url.searchParams.set('w', config.width.toString());
    }

    // When requesting a placeholder image we ask a low quality image to reduce the load time.
    if (config.isPlaceholder) {
      url.searchParams.set('q', PLACEHOLDER_QUALITY.toString());
    }

    // Allows users to add any Imgix transformation parameters as object
    if (typeof config.loaderParams?.['transform'] === 'object') {
      Object.entries(config.loaderParams?.['transform']).forEach(
        ([key, value]: [string, any]) => {
          url.searchParams.set(key, value?.toString ? value.toString() : value);
        }
      );
    }

    return url.href;
  };
}
